import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { format, differenceInCalendarDays } from "date-fns";
import toast from 'react-hot-toast';
import { 
  useTheme, 
  useMediaQuery, 
  Box, 
  Typography,   
  CircularProgress, 
  Tooltip, 
  IconButton,
  FormControl, 
  MenuItem, 
  InputLabel, 
  Select, 
} from '@mui/material';
import {
  Visibility,
  DownloadForOffline,
} from '@mui/icons-material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from "state/axios";
import { fetchGroupedRosterDetailsByCrewCode } from 'state/api';
import { hasPermission } from 'state/checkPermission';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import FilterButton from 'components/FilterButton';
import NumberControl from 'components/NumberControl';
import DataGridComponent from 'components/DataGridComponent';
import RosterDetailsDialog from 'components/RosterDetailsDialog';
import CrewFilterDialog from '../crewMembers/CrewFilterDialog';
import AnnualLeavesList from './AnnualLeavesList';


const AnnualLeaves = () => {
  useAuthCheck();
  const user = useSelector((state) => state.user); 
  const crewRanksData = useSelector((state) => state.crewRanks);
  const theme = useTheme();  
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const canView = hasPermission(user?.permissions, "AnnualLeaves", "view", false);
  const canAllocate = hasPermission(user?.permissions, "AnnualLeaves", "allocate", false);
  
  const { startDate, endDate } = useDateRangeContext();

  const scrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState(() => ({ isActive: 'ACTIVE' }));
  const defaultFilters = {
    isActive: 'ACTIVE',
  };
  const [openRosterDetailsDialog, setOpenRosterDetailsDialog] = useState(false);
  
  const [selectedCrewCode, setSelectedCrewCode] = useState(null);
  const [selectedCrewName, setSelectedCrewName] = useState(null);
  const [crewRosters, setCrewRosters] = useState([]);
  const [crewMembers, setCrewMembers] = useState([]);  
  const [crewScheduleCodes, setCrewScheduleCodes] = useState([]);
  const [selectedScheduleCode, setSelectedScheduleCode] = useState('');
  // const [rosterDetails, setRosterDetails] = useState([]);
  const [assignableDays, setAssignableDays] = useState(3);
  const [minALDays, setMinALDays] = useState(6);
  

  const fetchCrewScheduleCodes = useCallback(async () => {
    return await api.get(`/crewschedulecodes`);
  }, []);
  
  const fetchCrewMembers = useCallback(async () => {
    if (!startDate || !endDate) return [];

    const url = `/annualleaves`;
    const payload = { startDate, endDate }    
    return await api.post(url, payload);
  }, [startDate, endDate]);

  const fetchRosterDetails = useCallback(async () => {
    if (!startDate || !endDate) return [];

    const url = `/rosterdetails/rosterdetailsforannualleaves`;
    const payload = { startDate, endDate }    
    return await api.post(url, payload);
  }, [startDate, endDate]);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setError(null);
        setLoading(true);
        
        const [
          crewScheduleCodesResponse, 
          crewMembersResponse, 
          // rosterDetailsResponse, 
        ] = await Promise.all([
          fetchCrewScheduleCodes(),
          fetchCrewMembers(),
          // fetchRosterDetails(),
        ]);
        
        const scheduleCodes = crewScheduleCodesResponse?.data ?? [];
        const filteredCodes = scheduleCodes?.filter(item => item.category === "ANL");
        setCrewScheduleCodes(filteredCodes);

        const crewList = crewMembersResponse.data?.map(m => {
          const contactTel = m['contactTel'] || m['contactCell'];
          return {
            ...m,
            contactTel,
          };
        });
  
        setCrewMembers(crewList);           
        
        // setRosterDetails(rosterDetailsResponse?.data || [])
      } catch (error) {
        setError(error);
        toast.error(error?.message || "Failed to fetch data!");
      } finally {
        setLoading(false);
      }
    };

    fetchData();      
  }, [
    fetchCrewScheduleCodes,
    fetchCrewMembers, 
    // fetchRosterDetails,
  ]); 
  
  const filteredCrewMembers = useMemo(() => {
    if (!Array.isArray(crewMembers)) {
      return []; 
    }
    
    if (!filters) return crewMembers;

    return crewMembers.filter((crewMember) => {
      return Object.entries(filters).every(([key, value]) => {
        if (!value || (Array.isArray(value) && value.length === 0)) return true;
        
        if (key === 'crewCode') {
          return crewMember[key] === Number(value); 
        }

        if (key === 'crewName' || key === 'quals' || key === 'email') {
          return crewMember[key]?.toLowerCase().includes(value.toLowerCase()); 
        }

        if (key === 'birthDay') {
          const formattedBirthDay = crewMember[key] ? format(new Date(crewMember[key]), 'yyyy-MM-dd') : null;
          const formattedFilterDate = value ? format(new Date(value), 'yyyy-MM-dd') : null;
          return formattedBirthDay === formattedFilterDate;
        }

        if (key === 'gender') {
          if (value === 'ALL') {
            return true
          } else {
            return crewMember[key] === value;
          };
        }

        if (key === 'location' || key === 'rank') {
          return Array.isArray(value) ? value.includes(crewMember[key]) : crewMember[key] === value;
        }

        if (key === 'trainingQuals') {
          return Array.isArray(value)
            ? value.some(v => (crewMember.trainingQuals || []).includes(v))
            : (crewMember.trainingQuals || []).includes(value);
        }

        if (key === 'isActive') {
          if (value === 'ALL') return true; 
          if (value === 'ACTIVE') return crewMember[key] === true; 
          if (value === 'INACTIVE') return crewMember[key] === false; 
        }

        return crewMember[key] === value;
      });
    });
  }, [crewMembers, filters]);   

  const handleFilterOpen = () => setIsFilterDialogOpen(true);
  const handleFilterClose = () => setIsFilterDialogOpen(false);
  
  const handleFilterApply = (filterValues) => {
    setFilters(filterValues); 
  };

  const handleMinALDaysChange = (val) => {
    setMinALDays(val); 
  };

  const allocateAnnualLeaves = async () => {
    try {
      if (!startDate || !endDate) {
        toast.error("Invalid date range!");
        return;
      }

      if (!selectedScheduleCode) {
        toast.error("Please select annual leave code!");
        return;
      }

      const rosterDetailsResponse = await fetchRosterDetails();
      const rosterDetails = rosterDetailsResponse?.data || [];

      const rosterDetailsSet = new Set(
        rosterDetails.map(
          (r) =>
            `${r.crewCode}_${format(new Date(r.flightDate), "ddMMyyyy")}`
        )
      );

      const crewScheduleSet = rosterDetails.reduce((set, r) => {
        if (!r.routeDate) {
          set.add(`${r.crewCode}_${r.flightNo}`);
        }
        return set;
      }, new Set());

      let result = [];

      const totalDays = differenceInCalendarDays(endDate, startDate) + 1;

      filteredCrewMembers.forEach((member, index) => {
        const { crewCode, remainingALs } = member;

        let currentRemainingALs = remainingALs;

        if (currentRemainingALs < minALDays) return;
        if (crewScheduleSet.has(`${crewCode}_${selectedScheduleCode}`)) return;

        const maxAssignable = Math.min(assignableDays, currentRemainingALs);

        const offset = (index * maxAssignable) % totalDays;

        for (let k = 0; k < totalDays; k++) {
          const i = (offset + k) % totalDays;

          let tempDates = [];
          let isValidBlock = true;

          let base = new Date(startDate);

          for (let j = 0; j < maxAssignable; j++) {
            let d = new Date(base);

            const dayIndex = (i + j) % totalDays;
            d.setDate(base.getDate() + dayIndex);

            const dateStr = format(d, "ddMMyyyy");
            const key = `${crewCode}_${dateStr}`;

            if (rosterDetailsSet.has(key)) {
              isValidBlock = false;
              break;
            }

            currentRemainingALs--;
            if (currentRemainingALs < minALDays) break;

            tempDates.push(dateStr);
          }

          if (isValidBlock) {
            tempDates.forEach((dateStr) => {
              result.push(`${crewCode},${dateStr},${selectedScheduleCode}`);              
            });
            break;
          }
        }       
      });

      if (result.length === 0) {
        toast.error("No data to export!");
        return;
      }

      const blob = new Blob([result.join("\n")], {
        type: "text/plain;charset=utf-8",
      });

      const fileName = `AL_${format(
        new Date(),
        "yyyyMMddHHmmss"
      )}.txt`;

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();

      toast.success(`Generated ${result.length} annual leave days.`);
    } catch (err) {
      toast.error(err?.message || "Failed to allocate annual leaves!");
    }
  };
  
  const handleCloseRosterDetailsDialog = () => {
    setOpenRosterDetailsDialog(false);
    setCrewRosters([]);
  };

  const handleViewClick = useCallback(async (param) => {    
    if (!startDate || !endDate) return [];

    try {
      const crewCode = param.crewCode;
      const crewName = param.crewName;
      setSelectedCrewCode(crewCode);
      setSelectedCrewName(crewName);

      const result = await fetchGroupedRosterDetailsByCrewCode(crewCode, startDate, endDate);
      
      setCrewRosters(result);
      setOpenRosterDetailsDialog(true);
    } catch (error) {
      setOpenRosterDetailsDialog(false);
    } 
  }, [
    startDate, 
    endDate, 
  ]); 

  const columns = useMemo(() => [    
    {
      field: "no",
      headerName: "No",
      filterable: false,      
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      ...(() => {
        return isMobile ? { width: 60 } : { flex: 1 };
      })(),
    },
    { 
      field: "crewCode", 
      headerName: "Code", 
      ...(() => {
        return isMobile ? { width: 80 } : { flex: 1 };
      })(),
    },
    {
      field: "crewName",
      headerName: "CrewName",
      cellClassName: "name-column--cell",
      ...(() => {
        return isMobile ? { width: 300 } : { flex: 4 };
      })(),
    },
    {
      field: "gender",
      headerName: "Gender",
      ...(() => {
        return isMobile ? { width: 80 } : { flex: 1.1 };
      })(),
    },
    {
      field: "birthday",
      headerName: "Birthday",
      valueFormatter: (value) => {
        if (value == null) {
          return '';
        }
        return `${format(value, "dd/MM/yyyy")}`;
      },
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(),
    },    
    {
      field: "nationality",
      headerName: "Nationality",
      ...(() => {
        return isMobile ? { width: 80 } : { flex: 1.2 };
      })(),
    },
    {
      field: "marital",
      headerName: "Marital",
      ...(() => {
        return isMobile ? { width: 80 } : { flex: 1.2 };
      })(),
    },
    {
      field: "rank",
      headerName: "Rank",
      ...(() => {
        return isMobile ? { width: 60 } : { flex: 1 };
      })(),
    },
    {
      field: "location",
      headerName: "Base",
      ...(() => {
        return isMobile ? { width: 80 } : { flex: 1 };
      })(),
    },    
    {
      field: "trainingQuals",
      headerName: "Trainer",
      ...(() => {
        return isMobile ? { width: 60 } : { flex: 1 };
      })(),
    }, 
    {
      field: "quals",
      headerName: "Quals",
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(),
    },
    {
      field: "contactTel",
      headerName: "ContactTel",
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(),
    },    
    {
      field: "email",
      headerName: "Email",
      ...(() => {
        return isMobile ? { width: 200 } : { flex: 3 };
      })(),
    },    
    {
      field: "takenLWOPs",
      headerName: "Taken LWOP",
      align: "right",
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(),
    },
    {
      field: "stdALs",
      headerName: "Standard ALs",
      align: "right",
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(),
      valueFormatter: (params) => {
        return params > 0 ? parseFloat(params).toFixed(1) : 0; 
      },
    }, 
    {
      field: "takenALs",
      headerName: "Taken ALs",
      align: "right",
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(),
      valueFormatter: (params) => {
        return params > 0 ? parseFloat(params).toFixed(0) : 0; 
      },
    },  
    {
      field: "remainingALs",
      headerName: "Remaining ALs",
      align: "right",
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(),
      renderCell: (params) => (
        <span style={{ color: params.value < 0 ? "orange" : "inherit" }}>
          {parseFloat(params.value).toFixed(1)}
        </span>
      ),
    },     
  ], [isMobile]); 

  const hiddenColumns = useMemo(() => ({
    _id: false,
    no: false,
    gender: false,
    birthday: false,
    location: false,
    nationality: false,
    marital: false,
    quals: false,
    trainingQuals: false,
    contactTel: false,
    email: false,
  }), []);

  const nonMobileHiddenColumns = useMemo(() => ({
    _id: false,
    gender: false,
    birthday: false,
    nationality: false,
    marital: false,
    quals: false,
    trainingQuals: false,
    contactTel: false,
    email: false,
  }), []);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    !isDesktop ? hiddenColumns : nonMobileHiddenColumns
  );

  useEffect(() => {
    setColumnVisibilityModel(!isDesktop ? hiddenColumns : nonMobileHiddenColumns);
  }, [isDesktop, hiddenColumns, nonMobileHiddenColumns]);

  const actionColumn = useMemo(() => {    
    const activeCount = [canView].filter(Boolean).length;
    if (activeCount === 0) return [];

    const baseWidth = 60 + activeCount * 25;

    return [
      {
        field: "action",
        headerName: "Action",
        filterable: false,
        sortable: false,
        width: baseWidth,
        renderCell: (params) => {
            return (
              <FlexCenter
                gap={1}
                sx={{ width: '100%', height: '100%' }}
              >
                <Tooltip title="View">
                  <IconButton
                    aria-label="View"
                    sx={{
                      color: theme.palette.primary.dark,
                      cursor: 'pointer',
                      '&:hover': {
                        color: theme.palette.green.deep,
                      }
                    }}
                    onClick={() => { 
                      if (!canView) return;
                      handleViewClick(params.row);
                    }}
                  >
                    <Visibility />
                  </IconButton>
                </Tooltip>
              </FlexCenter>
            );
        },
      }
    ]
  }, [
    theme, 
    canView, 
    handleViewClick
  ]);     

  const {
    genderOptions,
    baseOptions,
    rankOptions,
    trainingQualOptions,
    isActiveOptions,
  } = useMemo(() => {
    if (!Array.isArray(crewMembers) || crewMembers.length === 0) {
      return {
        genderOptions: [],
        baseOptions: [],
        rankOptions: [],
        trainingQualOptions: [],
        isActiveOptions: [],
      };
    }

    const genderSet = new Set();
    const baseSet = new Set();
    const rankSet = new Set();
    const trainingQualSet = new Set();
    const isActiveSet = new Set();

    for (const m of crewMembers) {
      m?.gender && genderSet.add(m.gender);
      m?.location && baseSet.add(m.location);
      m?.rank && rankSet.add(m.rank);
      m?.isActive !== undefined && isActiveSet.add(m.isActive);

      if (Array.isArray(m?.trainingQuals)) {
        for (const q of m.trainingQuals) {
          q && trainingQualSet.add(q);
        }
      }
    }

    // map rank O(1)
    const rankMap = new Map(
      (crewRanksData || []).map(r => [r.rankCode, r])
    );

    return {
      genderOptions: [...genderSet].sort(),
      baseOptions: [...baseSet].sort(),
      rankOptions: [...rankSet]
        .map(r => rankMap.get(r))
        .filter(Boolean)
        .sort((a, b) => a.rankPriority - b.rankPriority)
        .map(r => r.rankCode),

      trainingQualOptions: [...trainingQualSet].sort(),
      isActiveOptions: [...isActiveSet].sort(),
    };
  }, [crewMembers, crewRanksData]);
  
  const sortedMobileRows = useMemo(() =>
    [...filteredCrewMembers].sort((a, b) => (a.remainingALs ?? 0) - (b.remainingALs ?? 0)),
    [filteredCrewMembers]
  );


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="Annual Leaves" 
          subtitle={`${loading ? 'Loading...' : 'List of Annual Leaves'}`}
        /> 
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            {canAllocate && !isMobile && (
              <Box display="flex" alignItems="center" gap={1}>
                <FormControl
                  sx={{ width: isMobile ? "9rem" : "12rem" }}
                  size="small"
                >
                  <InputLabel id="annual-leave-code-label">Annual Leave Code</InputLabel>
                  <Select
                    label="Annual Leave Code"
                    value={selectedScheduleCode}
                    onChange={(e) => setSelectedScheduleCode(e.target.value)}
                  >
                    {crewScheduleCodes?.map((item, index) => {
                      return (
                      <MenuItem key={index} value={item.code}>
                        {item.code} {item.description ? ` - ${item.description}` : ""}
                      </MenuItem>
                      )
                    })}
                  </Select>
                </FormControl>
                
                <Tooltip title="Number of ALs to allocate" arrow>
                  <span>
                    <NumberControl
                      min={1}
                      max={16}
                      value={assignableDays}
                      onChange={(val) => setAssignableDays(val)} 
                      textFieldWidth={45}
                    />
                  </span>
                </Tooltip>

                <Tooltip title="Threshold to allocate" arrow>
                  <span>
                    <NumberControl
                      min={1}
                      max={16}
                      value={minALDays}
                      onChange={handleMinALDaysChange} 
                      textFieldWidth={45}
                    />
                  </span>
                </Tooltip>
                
                <Tooltip title="Generate Annual Leaves" arrow>
                  <span>
                    <StyledButton 
                      onClick={() => {
                        allocateAnnualLeaves();
                      }}
                    >
                      <DownloadForOffline />
                    </StyledButton>
                  </span>
                </Tooltip>                
              </Box>
            )}
            
            <FilterButton
              filters={filters}
              defaultFilters={defaultFilters}
              onClick={handleFilterOpen}
            />
          </Box>
        )}                
      </FlexBetween>            
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ""}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
        >
          <Box
            ref={scrollRef} 
            height={LAYOUT_HEIGHT}
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
          >
            {isMobile ? (
              <AnnualLeavesList
                rows={sortedMobileRows}
                canView={canView}
                onViewClick={handleViewClick}
              />
            ) : (
              <DataGridComponent
                rows={filteredCrewMembers}
                columns={[...columns, ...actionColumn]}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={loading}
                props={{
                  initialState: { pagination: { paginationModel: { page: 0, pageSize: 100 } } },
                }}
              />
            )}

            <ScrollToTop scrollRef={scrollRef} />
          </Box>
          
          {isFilterDialogOpen &&
            <CrewFilterDialog
              open={isFilterDialogOpen}
              onClose={handleFilterClose}
              onApply={handleFilterApply}
              genderOptions={genderOptions}
              baseOptions={baseOptions}
              rankOptions={rankOptions}
              trainingQualOptions={trainingQualOptions}
              isActiveOptions={isActiveOptions}
              currentFilters={filters}
            />
          }
          
          {openRosterDetailsDialog &&
            <RosterDetailsDialog 
              open={openRosterDetailsDialog}
              onClose={handleCloseRosterDetailsDialog}
              crewCode={selectedCrewCode}
              crewName={selectedCrewName}
              crewRosters={crewRosters}
            />
          }            
        </Box>
      )}
    </Box>
  );
};

export default AnnualLeaves;
